import React, { useRef, useMemo } from 'react';

// 引入柱状图
import 'echarts/lib/chart/line';
// 引入提示框和标题组件
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
// 使用样式
import 'echarts/theme/macarons';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import {
  percentageFormat,
  reviewsFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';
import {
  arrayToSimpleObj,
  getPercentageValue,
  sortMapAuxiliaryFunc,
} from '@/pages/opinion/utils';
import { getLocale, useIntl } from 'umi';
import {
  formatTimeXAxisText,
  setIntervalValue,
} from '@/pages/opinion/utils/chart';
import { formatDateFunc } from '@/pages/opinion/utils/time';

export default function MentionsSentimentChart({
  mentionsList,
  dateType = 'daily',
  gameType = 'mobile',
}: any) {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const echartsRef = useRef(null);
  const formatTime = formatDateFunc(dateType);

  const totalMap = useMemo(() => {
    return arrayToSimpleObj(mentionsList, 'count', (item: any) =>
      formatTime(item.time),
    );
  }, [mentionsList, dateType]);

  const sortMap = {
    'Avg. Sentiment': 1,
    Positive: 2,
    Neutral: 3,
    Negative: 4,
  };

  /**
   * 设置ehcarts 数据
   */
  function getEchartsData() {
    const option = {
      tooltip: {
        formatter: (params: any) => {
          const letTitle = listTimeTitleFunc(params[0]?.axisValue, dateType);
          const totalKey = formatTime(params[0]?.axisValue);

          const total = totalMap?.[totalKey] || 0;
          return formatChartParamsDate({
            params: params,
            title: letTitle,
            total: total,
            valueFormat: reviewsFormat,
            dealwithRecords(records, total) {
              let newRecords = records.map(
                ({ originValue, originText, text, value }) => {
                  if (originText === formatMessage({ id: 'Avg. Sentiment' })) {
                    value = sentimentFormat(originValue);
                  } else {
                    value =
                      originValue === 0
                        ? '-'
                        : `${originValue}(${percentageFormat(
                            getPercentageValue(originValue, total, 6) * 100,
                          )})`;
                  }

                  return { text, value, originText };
                },
              );

              newRecords.sort(
                sortMapAuxiliaryFunc(sortMap, (item) => item.originText),
              );
              newRecords.unshift({
                text: formatMessage({ id: 'News Mentions' }),
                value: reviewsFormat(total),
                originText: formatMessage({ id: 'News Mentions' }),
              });
              newRecords = newRecords.filter(
                (item) => item.originText !== 'total总数',
              );
              return newRecords;
            },
          });
        },
      },
      grid: {
        right: '0%',
        bottom: '10%',
        top: 20,
      },
      legend: {
        data: [
          formatMessage({ id: 'Avg. Sentiment' }),
          formatMessage({ id: 'Positive' }),
          formatMessage({ id: 'Neutral' }),
          formatMessage({ id: 'Negative' }),
        ],
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, dateType);
      }),
      xAxis: {
        axisLabel: {
          formatter: (value: any) => formatTimeXAxisText(value, dateType),
        },
        data: [],
      },
      yAxis: [
        {
          type: 'value',
        },
        {
          type: 'value',
          max: 5,
          min: 0,
          splitNumber: 5,
        },
      ],
      series: [
        {
          name: formatMessage({ id: 'Avg. Sentiment' }),
          type: 'line',
          yAxisIndex: 1,
          lineStyle: {
            color: '#5086F3',
          },
          data: [],
        },
        {
          name: 'total总数',
          type: 'bar',
          barMaxWidth: '70px', // 柱子宽度同实体柱子，否则会错位
          barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
          label: {
            normal: {
              show: true,
              position: 'top', // 位置设为top
              textStyle: { color: '#444' },
            },
          },
          itemStyle: {
            normal: {
              color: 'rgba(128, 128, 128, 0)', // 为透明
            },
          },
          data: [],
        },
        {
          name: formatMessage({ id: 'Positive' }),
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: [],
          itemStyle: {
            color: '#69D19C',
          },
          barMaxWidth: '70px',
        },
        {
          name: formatMessage({ id: 'Neutral' }),
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: [],
          itemStyle: {
            color: '#6E7794',
          },
          barMaxWidth: '70px',
        },
        {
          name: formatMessage({ id: 'Negative' }),
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: [],
          itemStyle: {
            color: '#F17C86',
          },
          barMaxWidth: '70px',
        },
      ],
    };

    if (mentionsList) {
      const avg: any = [];
      const negative: any = [];
      const neutraul: any = [];
      const positive: any = [];
      const time: any = [];
      const offsetWidth: number = echartsRef?.current?.offsetWidth;
      mentionsList.map(
        (item: {
          sentiment_avg: string;
          negative_mentions: any;
          neutral_mentions: any;
          positive_mentions: any;
          time: any;
        }) => {
          avg.push(item.sentiment_avg === '' ? '0.00' : item.sentiment_avg);
          negative.push(item.negative_mentions);
          neutraul.push(item.neutral_mentions);
          positive.push(item.positive_mentions);
          time.push(item.time);
        },
      );
      option.xAxis.data = time;
      const total = mentionsList.map((m) => m?.count);
      // 小屏1366px且柱子数大于10，或者柱子数大于16根，不显示汇总值
      if (
        (offsetWidth <= 636 && mentionsList.length > 10) ||
        mentionsList.length > 16
      ) {
        option.series[1].data = []; // 堆叠柱状图顶部的总数
      } else {
        option.series[1].data = total; // 堆叠柱状图顶部的总数
      }
      option.series[2].data = positive;
      option.series[3].data = neutraul;
      option.series[4].data = negative;
      option.series[0].data = avg;

      setIntervalValue(total, option.yAxis[0]);
    }

    return option;
  }

  const options = useMemo(() => {
    return getEchartsData();
  }, [dateType, gameType, mentionsList, locale]);

  return (
    <>
      <EChartCommon
        ref={echartsRef}
        leftTitle={formatMessage({ id: 'News Mentions' })}
        rightTitle={formatMessage({ id: 'Sentiment' })}
        height={300}
        options={options}
        optionsProps={{ type: 'bar' }}
      ></EChartCommon>
    </>
  );
}
